import { useEffect, useState } from 'react';
import { wrappers, hysteriFetch } from 'hysterics';
import logger from 'ws-scripts/modules/logger';

/**
 * Same-origin endpoint listing the web integrations configured for this site,
 * keyed by name (`mywallet`, `uds`, ...). Each value holds that integration's
 * settings, including the `fields` map edited in the WIC tool. A missing key
 * means the site doesn't have that integration.
 *
 * Send a bare GET: `filter` and `deviceType` are accepted but ignored
 * (measured -- byte-identical response), so passing them only splits the
 * browser cache. The dealer page already requests this URL itself, and the
 * response is cacheable for 10 minutes, so our call is usually a cache hit.
 */
export const WEB_INTEGRATION_SERVICES_URL = '/api/ipp/config/services';

/**
 * Per-attempt budget, overriding hysterics' 600ms default. The measured cold
 * response is ~340ms, which leaves too little headroom. Generous on purpose:
 * nothing reads the result until a shopper opens the drawer, so a slow answer
 * costs nothing, whereas giving up early throws away a signal we can't get
 * back.
 *
 * Unlike this widget's server-side fetches (`fetchNavData.js`,
 * `fetchDealerDepartments.js`) there is no prod/nonprod split here:
 * `ENVIRONMENT_NAME` is server-only and isn't defined in the client bundle.
 */
const TIMEOUT_MS = 2000;

/**
 * Module-level singleton, following `fetchNavData.js` and hysterics' own
 * guidance -- building the wrapper inside the hook would reset its
 * fault-tolerance state on every render, and hysterics logs a warning if the
 * same service name is registered twice.
 *
 * Only the timeout is configured, because the rest of the default wrapper
 * chain already does what this hook used to hand-roll:
 *
 *   - `fetch` parses the JSON body and throws `FetchHttpError` on a non-OK
 *     status, so there is no `response.ok` check or `.json()` call here.
 *   - `abort` owns the `AbortController` and hands its signal to `fetch`.
 *   - `retry` gives one free retry on a network-level failure (not on a 4xx).
 *     Worth keeping: this hook fails closed, so a transient blip would
 *     otherwise hide the My Wallet button for the whole page view.
 *
 * No circuit-breaker options: hysterics does not support the circuit breaker
 * in its browser build, so configuring one here would be dead weight. That is
 * the one deliberate difference from `fetchNavData.js`, which runs on the
 * server and does configure it.
 */
const webIntegrationServicesFetch = hysteriFetch(
	'nav-drawer-web-integration-services',
	{
		[wrappers.timeout.contextKey]: { timeout: TIMEOUT_MS }
	}
);

/**
 * Reads one web integration's configuration from the site's own endpoint.
 *
 * `integration` is that integration's config once the endpoint answers, and
 * `null` otherwise. "This site doesn't have it" and "no answer yet" (in
 * flight, failed, timed out, non-OK) are deliberately the same `null`: with no
 * answer, a caller has no basis to act as though a setting were switched on.
 * Callers therefore fail closed for free -- `integration?.fields?.x === true`
 * is false in every unresolved case. Anything that genuinely needs to tell
 * "absent" from "unknown" apart should extend this hook rather than
 * reinterpret `null`.
 *
 * Fetched once per mount, never on drawer open/close. Fetching lazily would
 * skip the request on sites without the integration, but a ~340ms answer
 * landing after the drawer is already open would pop a button into a row the
 * shopper is looking at -- the flash bug fixed in `8ddf2cf`.
 *
 * @param {string} integrationName - key to look up, e.g. 'mywallet'
 * @returns {{ integration: object|null }}
 */
const useWebIntegrationConfig = (integrationName) => {
	const [integration, setIntegration] = useState(null);

	useEffect(() => {
		// Guards setState after unmount. It does not cancel the request itself:
		// hysteriFetch's executor is an async function, so the `abort` wrapper's
		// `cancel()` is attached to an inner promise we never receive. An
		// orphaned same-origin GET is a fair price for using the shared client.
		let cancelled = false;

		const probe = async () => {
			try {
				const services = await webIntegrationServicesFetch(
					WEB_INTEGRATION_SERVICES_URL,
					{ headers: { Accept: 'application/json' } }
				);

				if (cancelled) return;
				setIntegration(services?.[integrationName] ?? null);
			} catch (error) {
				// No setState: `integration` stays `null`, which is exactly the
				// fail-closed contract above. Logged rather than swallowed so a
				// persistently failing endpoint is diagnosable.
				if (cancelled) return;
				logger.warn(
					`[useWebIntegrationConfig] ${WEB_INTEGRATION_SERVICES_URL} lookup for '${integrationName}' failed: ${error?.message}`
				);
			}
		};

		probe();

		return () => {
			cancelled = true;
		};
	}, [integrationName]);

	return { integration };
};

export default useWebIntegrationConfig;
